import { create } from "zustand";
import fetcher from "@/forApp/utils/fetcher/fetcher";

export type PropertyType = {
	slug: string;
	name: string;
	entityTypes?: PropertyType[];
};

interface PropertyState {
	types: PropertyType[];
	lastFetched: number | null;
	fetchTypes: () => Promise<void>;
	getSubtypes: (typeSlug: string) => PropertyType[];
	getTypeBySlug: (slug: string) => PropertyType | undefined;
	reset: () => void;
}

const CACHE_EXPIRATION = 5 * 60 * 1000;

export const useProperty = create<PropertyState>()((set, get) => ({
	types: [],
	lastFetched: null,

	fetchTypes: async () => {
		const { lastFetched, types } = get();

		if (
			lastFetched &&
			types.length > 0 &&
			Date.now() - lastFetched < CACHE_EXPIRATION
		) {
			return;
		}

		try {
			const response = await fetcher<PropertyType[]>(
				"GET",
				"/groups-entity-types",
			);

			if (response.success && response.data) {
				set({
					types: response.data,
					lastFetched: Date.now(),
				});
			}
		} catch (error) {
			console.error("Error fetching property types:", error);
		}
	},

	getSubtypes: (typeSlug: string) => {
		const { types } = get();
		return types.find((t) => t.slug === typeSlug)?.entityTypes || [];
	},

	getTypeBySlug: (slug: string) => {
		const { types } = get();

		const topLevelType = types.find((t) => t.slug === slug);
		if (topLevelType) return topLevelType;

		for (const type of types) {
			if (type.entityTypes) {
				const subtype = type.entityTypes.find((st) => st.slug === slug);
				if (subtype) return subtype;
			}
		}

		return undefined;
	},

	reset: () => {
		set({
			types: [],
			lastFetched: null,
		});
	},
}));
